import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SectionTitle from '../ui/SectionTitle';
import SEO from '../seo/SEO';
import { getQuizBySlug } from '../../lib/quizzes';
import { resolveSharedResult, CalculatedResult } from '../../lib/personalityQuiz';
import QuizEngine from './QuizEngine';
import QuizResultView from './QuizResultView';
import { fadeInUp } from '../../lib/animations';

const QuizPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const quiz = slug ? getQuizBySlug(slug) : undefined;

  // Local state for the current user's session quiz state
  const [result, setResult] = useState<CalculatedResult | null>(null);
  const [userName, setUserName] = useState('');

  // Extract shared state from query parameters if present
  const queryName = searchParams.get('name');
  const queryResult = searchParams.get('result');

  const isShared = Boolean(queryName && queryResult);

  // Attempt to resolve shared results on mount/param change
  const [sharedResult, setSharedResult] = useState<CalculatedResult | null>(null);

  useEffect(() => {
    if (quiz && queryResult) {
      const resolved = resolveSharedResult(quiz, queryResult);
      if (resolved) {
        setSharedResult(resolved);
      }
    } else {
      setSharedResult(null);
    }
  }, [quiz, queryResult]);

  if (!quiz) {
    return <Navigate to="/quiz" replace />;
  }

  const handleComplete = (calculatedResult: CalculatedResult, name: string) => {
    setResult(calculatedResult);
    setUserName(name);
  };

  const handleRetake = () => {
    // If we're retaking in a shared view, clear search parameters to start a fresh test!
    if (isShared) {
      setSearchParams({});
    }
    setResult(null);
    setSharedResult(null);
  };

  // Determine metadata based on view type
  const pageTitle = isShared && queryName && sharedResult
    ? `${queryName} é uma mistura de ${sharedResult.shortName}! Faça o teste também – Gas2`
    : `${quiz.title} – Teste de Personalidade Gas2`;

  const pageDescription = isShared && queryName && sharedResult
    ? `Meu amigo(a) ${queryName} fez o teste "${quiz.title}" e deu ${sharedResult.shortName}! Clique para ver o resultado completo e descubra o seu.`
    : quiz.tldr;

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        image={quiz.thumbnailUrl}
      />

      <Navbar solid />

      <main className="min-h-screen bg-slate-950 pt-24 pb-20">
        {/* Navigation & Header */}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 mb-10">
          <Link
            to="/quiz"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para Quizzes
          </Link>

          {!result && !sharedResult && (
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              className="text-center sm:text-left"
            >
              <span className="text-xs font-bold uppercase tracking-wider text-amber-400 bg-amber-400/10 px-3 py-1 rounded-full border border-amber-400/20 mb-3 inline-block">
                Quiz Interativo
              </span>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white leading-tight mb-3">
                {quiz.title}
              </h1>
              <p className="text-slate-400 text-sm sm:text-base leading-relaxed max-w-2xl">
                {quiz.tldr}
              </p>
            </motion.div>
          )}
        </div>

        {/* Dynamic State views */}
        {sharedResult ? (
          <QuizResultView
            quiz={quiz}
            result={sharedResult}
            userName=""
            isSharedView={true}
            sharedBy={queryName ?? ''}
            onRetake={handleRetake}
          />
        ) : result ? (
          <QuizResultView
            quiz={quiz}
            result={result}
            userName={userName}
            onRetake={handleRetake}
          />
        ) : (
          <QuizEngine quiz={quiz} onComplete={handleComplete} />
        )}
      </main>

      <Footer />
    </>
  );
};

export default QuizPage;
